import React from 'react';
import { Translation } from 'react-i18next';

/**
 * Restores the function form of the old `withNamespaces` HOC, where the namespace was
 * derived per instance from props: `withNamespaces(props => props.resourcesNamespace)`.
 *
 * react-i18next v10 replaced `withNamespaces` with `withTranslation`, which only accepts a
 * namespace string or array. It forwards its argument straight to `useTranslation`, where
 * a non-string is used as `namespaces` and then hits `namespaces.every(...)` — so passing a
 * function throws rather than resolving the namespace.
 *
 * The assessment forms need this because their namespace varies by franchisor, so it is only
 * known from props at render time. `Translation` takes a plain `ns`, which lets the
 * namespace be resolved per instance before delegating.
 *
 * Props passed down match what `withTranslation` provides: `t`, `i18n` and `tReady`.
 */
const withResourcesNamespace = getNamespace => WrappedComponent => {
	const WithResourcesNamespace = props => (
		<Translation ns={getNamespace(props)}>
			{(t, { i18n }, ready) => (
				<WrappedComponent {...props} t={t} i18n={i18n} tReady={ready} />
			)}
		</Translation>
	);

	WithResourcesNamespace.displayName =
		`withResourcesNamespace(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	WithResourcesNamespace.WrappedComponent = WrappedComponent;

	return WithResourcesNamespace;
};

export default withResourcesNamespace;
